'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const heroSlides = [
{
  id: 'slide-wellness',
  headline: 'LIVE WELL.',
  sub: 'LOOK RADIANT.',
  accent: 'BUILD YOUR RICHÉSSE.',
  tag: 'Wellness Collection',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_191f10152-1764825393789.png",
  alt: 'Premium wellness products arranged on marble surface with soft natural lighting'
},
{
  id: 'slide-beauty',
  headline: 'RADIANCE',
  sub: 'REDEFINED.',
  accent: 'PREMIUM BEAUTY.',
  tag: 'Beauty Collection',
  image: "https://images.unsplash.com/photo-1734779754710-dede5160a417",
  alt: 'Luxury beauty products displayed on elegant white surface with gold accents'
},
{
  id: 'slide-lifestyle',
  headline: 'YOUR BRAND.',
  sub: 'YOUR STORE.',
  accent: 'YOUR FUTURE.',
  tag: 'Build Your Business',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1199e780e-1772095614390.png",
  alt: 'Confident entrepreneur working on laptop surrounded by premium lifestyle products'
}];


export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides?.length);
    }, 5500);
    return () => clearInterval(interval);
  }, []);

  const slide = heroSlides?.[currentSlide];

  return (
    <section className="relative h-screen min-h-[640px] max-h-[960px] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        {heroSlides?.map((s, i) =>
        <div
          key={s?.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === currentSlide ? 'opacity-100' : 'opacity-0'}`}>

            <AppImage
            src={s?.image}
            alt={s?.alt}
            fill
            priority={i === 0}
            className="object-cover"
            sizes="100vw" />

          </div>
        )}
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 bg-gradient-to-r from-accent/70 via-accent/30 to-transparent" />
      </div>
      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 w-full">
          <div className="max-w-2xl">
            {/* Tag */}
            <div
              className={`inline-flex items-center gap-2 mb-6 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: '100ms' }}>

              <span className="section-divider" />
              <span className="text-xs font-semibold tracking-widest uppercase text-primary">
                {slide?.tag}
              </span>
            </div>

            {/* Headline */}
            <h1
              className={`font-display text-hero-xl text-white mb-4 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '200ms' }}>

              <span className="block">{slide?.headline}</span>
              <span className="block text-white/80">{slide?.sub}</span>
              <span className="block text-gradient-gold">{slide?.accent}</span>
            </h1>

            {/* Subtext */}
            <p
              className={`text-base lg:text-lg text-white/70 mb-8 max-w-lg leading-relaxed transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '350ms' }}>

              A modern lifestyle brand empowering people through premium products, knowledge, community, and opportunity.
            </p>

            {/* CTAs */}
            <div
              className={`flex flex-wrap gap-4 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              style={{ transitionDelay: '450ms' }}>

              <Link href="/product-catalog" className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold">
                Explore Richésse
                <Icon name="ArrowRightIcon" size={16} />
              </Link>
              <Link href="/sign-up-login-screen" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold bg-white/10 backdrop-blur-sm text-white border border-white/20 hover:bg-white/20 transition-colors">
                Join the Movement
              </Link>
            </div>

            {/* Stats */}
            <div
              className={`flex flex-wrap gap-8 mt-12 transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: '550ms' }}>

              {[
              { id: 'stat-members', value: '48,000+', label: 'Active Members' },
              { id: 'stat-countries', value: '32', label: 'Countries' },
              { id: 'stat-products', value: '120+', label: 'Premium Products' }]?.
              map((stat) =>
              <div key={stat?.id}>
                  <p className="text-2xl font-bold text-white tabular-nums">{stat?.value}</p>
                  <p className="text-xs text-white/60 font-medium mt-0.5">{stat?.label}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
        {heroSlides?.map((s, i) =>
        <button
          key={`indicator-${s?.id}`}
          onClick={() => setCurrentSlide(i)}
          className={`transition-all duration-300 rounded-full ${
          i === currentSlide ? 'w-8 h-1.5 bg-primary' : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/70'}`
          }
          aria-label={`Go to slide ${i + 1}`} />

        )}
      </div>
      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-10 flex flex-col items-center gap-2 z-10 hidden lg:flex">
        <span className="text-[10px] font-semibold tracking-widest uppercase text-white/40 rotate-90 mb-4">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-white/40 to-transparent" />
      </div>
    </section>);

}